import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { useTaxonomy } from "@/lib/taxonomy";
import { SearchCombobox } from "@/components/SearchCombobox";

export default function OSNew() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [equipments, setEquipments] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const { disciplines } = useTaxonomy();
  const [form, setForm] = useState({
    equipment_tag: "",
    sector: "",
    discipline: "",
    maintenance_type: "Corretiva",
    priority: "Média",
    description: "",
    requester_name: user?.name || "",
    technician_ids: [],
    recommended_actions: "",
    parts_used: "",
  });

  useEffect(() => {
    api.get("/equipments").then((r) => setEquipments(r.data));
    api.get("/users/technicians").then((r) => setTechnicians(r.data));
  }, []);

  const setEq = (tag) => {
    const eq = equipments.find((e) => e.tag === tag);
    setForm((f) => ({ ...f, equipment_tag: tag, sector: eq?.sector || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.equipment_tag) return toast.error("Selecione o equipamento");
    if (!form.discipline) return toast.error("Selecione a disciplina");
    if (!form.description.trim()) return toast.error("Descreva o problema");
    setLoading(true);
    try {
      const { data } = await api.post("/os", form);
      toast.success(`OS #${data.number} criada com sucesso`);
      nav(`/os/${data.id}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="os-new">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => nav(-1)} className="rounded-sm" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Abrir Chamado</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Nova Ordem de Serviço</h1>
        </div>
      </div>

      <form onSubmit={submit}>
        <Card className="rounded-sm shadow-none p-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label>Equipamento / TAG *</Label>
              <div className="mt-1.5">
              <SearchCombobox
                items={equipments.map((e) => ({
                  value: e.tag,
                  search: `${e.tag} ${e.name} ${e.sector}`,
                  render: <span><span className="font-mono-tag mr-2 text-orange-600">{e.tag}</span> {e.name} — {e.sector}</span>,
                }))}
                value={form.equipment_tag}
                selectedLabel={(() => { const e = equipments.find((x) => x.tag === form.equipment_tag); return e ? `${e.tag} — ${e.name} (${e.sector})` : ""; })()}
                onChange={setEq}
                placeholder="Digite a TAG ou nome do equipamento"
                emptyText="Nenhum equipamento encontrado"
                testId="field-equipment"
              />
              </div>
            </div>
            <div>
              <Label>Setor</Label>
              <Input value={form.sector} disabled className="rounded-sm mt-1.5 bg-secondary" />
            </div>
            <div>
              <Label>Disciplina *</Label>
              <Select value={form.discipline} onValueChange={(v) => setForm({ ...form, discipline: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="field-discipline"><SelectValue placeholder="Selecione a disciplina" /></SelectTrigger>
                <SelectContent>
                  {disciplines.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo de Manutenção *</Label>
              <Select value={form.maintenance_type} onValueChange={(v) => setForm({ ...form, maintenance_type: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="field-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Corretiva">Corretiva</SelectItem>
                  <SelectItem value="Preventiva">Preventiva</SelectItem>
                  <SelectItem value="Preditiva">Preditiva</SelectItem>
                  <SelectItem value="Inspeção">Inspeção</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prioridade</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="field-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Baixa">Baixa</SelectItem>
                  <SelectItem value="Média">Média</SelectItem>
                  <SelectItem value="Alta">Alta</SelectItem>
                  <SelectItem value="Crítica">Crítica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Solicitante *</Label>
              <Input value={form.requester_name} onChange={(e) => setForm({ ...form, requester_name: e.target.value })} required className="rounded-sm mt-1.5" data-testid="field-requester" />
            </div>
            <div>
              <Label>Técnicos Responsáveis</Label>
              <div className="mt-1.5 space-y-1.5 border rounded-sm p-2 max-h-40 overflow-y-auto bg-background">
                {technicians.length === 0 && (
                  <div className="text-xs text-muted-foreground p-1">
                    Nenhum técnico cadastrado. Peça para se registrar com função "Técnico".
                  </div>
                )}
                {technicians.map((t) => {
                  const ids = form.technician_ids || [];
                  const checked = ids.includes(t.id);
                  return (
                    <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-secondary p-1 rounded-sm">
                      <input
                        type="checkbox"
                        checked={checked}
                        data-testid={`new-tech-checkbox-${t.id}`}
                        onChange={(e) => {
                          const next = new Set(ids);
                          if (e.target.checked) next.add(t.id); else next.delete(t.id);
                          setForm({ ...form, technician_ids: Array.from(next) });
                        }}
                        className="w-4 h-4 rounded-sm accent-orange-500"
                      />
                      <span className="flex-1">{t.name}</span>
                      <span className="text-xs text-muted-foreground">{t.email}</span>
                    </label>
                  );
                })}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">Selecione um ou mais técnicos (opcional). Todos recebem notificação.</div>
            </div>
          </div>

          <div>
            <Label>Descrição do Problema *</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} required className="rounded-sm mt-1.5" placeholder="Descreva o problema em detalhes: sintomas, ruídos, alarmes, etc." data-testid="field-description" />
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label>Ações Recomendadas</Label>
              <Textarea value={form.recommended_actions} onChange={(e) => setForm({ ...form, recommended_actions: e.target.value })} rows={3} className="rounded-sm mt-1.5" placeholder="Lista de tarefas sugeridas" data-testid="field-actions" />
            </div>
            <div>
              <Label>Peças / Materiais</Label>
              <Textarea value={form.parts_used} onChange={(e) => setForm({ ...form, parts_used: e.target.value })} rows={3} className="rounded-sm mt-1.5" placeholder="Lista de suprimentos" data-testid="field-parts" />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => nav(-1)} className="rounded-sm">Cancelar</Button>
            <Button type="submit" disabled={loading} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="submit-os">
              <Save className="w-4 h-4" /> {loading ? "Criando..." : "Criar OS"}
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
